import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { useTheme } from "@mui/material/styles";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  LineElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { useState } from "react";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import { useGetBeautyDashboardQuery } from "slices/addOnApi";

import SpaIcon from "@mui/icons-material/Spa";
import GroupIcon from "@mui/icons-material/Group";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import ChatIcon from "@mui/icons-material/Chat";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import InventoryIcon from "@mui/icons-material/Inventory";

ChartJS.register(
  BarElement,
  LineElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Filler,
);

// ── helpers ───────────────────────────────────────────────────
const SC = ({ children }) => (
  <Typography
    variant="h6"
    fontWeight={700}
    mb={2}
    sx={{ color: "#442141", borderLeft: "4px solid #FF6584", pl: 1.5 }}
  >
    {children}
  </Typography>
);

const KpiCard = ({
  icon,
  label,
  value,
  sub,
  subColor = "#22c55e",
  bg = "#fff",
}) => (
  <Card
    sx={{
      p: 2.5,
      borderRadius: 3,
      height: "100%",
      background: bg,
      boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
    }}
  >
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          textTransform="uppercase"
          letterSpacing={1}
        >
          {label}
        </Typography>
        <Typography variant="h4" fontWeight={700} mt={0.5}>
          {value ?? 0}
        </Typography>
        {sub && (
          <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
            <TrendingUpIcon sx={{ fontSize: 13, color: subColor }} />
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ color: subColor }}
            >
              {sub}
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          background: "rgba(255,101,132,0.12)",
          borderRadius: 2,
          p: 1.2,
          display: "flex",
        }}
      >
        {icon}
      </Box>
    </Box>
  </Card>
);

const statusMap = {
  active: "success",
  inactive: "warning",
  blocked: "error",
  deleted: "error",
  completed: "success",
  cancelled: "error",
  "in-progress": "warning",
  pending: "warning",
  paid: "success",
  failed: "error",
};
const SC2 = (s) => (
  <Chip
    label={s}
    size="small"
    color={statusMap[s] || "default"}
    sx={{ fontWeight: 600, fontSize: 11 }}
  />
);

const TH = ({ cols }) => (
  <TableHead>
    <TableRow sx={{ background: "#fdf0f5" }}>
      {cols.map((c) => (
        <TableCell
          key={c}
          sx={{
            fontWeight: 700,
            fontSize: 12,
            color: "#442141",
            whiteSpace: "nowrap",
            px: 2,
            py: 1,
          }}
        >
          {c}
        </TableCell>
      ))}
    </TableRow>
  </TableHead>
);

const chartOpts = (theme) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: { color: theme.palette.text.primary, font: { size: 11 } },
    },
    tooltip: { mode: "index", intersect: false },
  },
  scales: {
    x: {
      ticks: { color: theme.palette.text.secondary, font: { size: 10 } },
      grid: { color: theme.palette.divider },
    },
    y: {
      beginAtZero: true,
      ticks: { color: theme.palette.text.secondary, font: { size: 10 } },
      grid: { color: theme.palette.divider },
    },
  },
});

// ── component ─────────────────────────────────────────────────
function BeautyDashboard() {
  const theme = useTheme();
  const [citySalesPeriod, setCitySalesPeriod] = useState("month");
  const { data, isLoading, isError } =
    useGetBeautyDashboardQuery(citySalesPeriod);

  if (isLoading)
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress sx={{ color: "#FF6584" }} />
        </Box>
        <Footer />
      </DashboardLayout>
    );

  if (isError || !data)
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <Typography color="error">
            Failed to load beauty dashboard.
          </Typography>
        </Box>
        <Footer />
      </DashboardLayout>
    );

  const {
    beauticianStats,
    beauticianLogins,
    beauticianCityCount,
    latestBeauticians,
    latestBeautyOrders,
    monthlyEarnings,
    walletStats,
    serviceCounts,
    callChatStats,
    orderSummary,
    citySales,
  } = data;

  // ── chart data ──────────────────────────────────────────────
  const months = monthlyEarnings?.map((d) => d.month) || [];

  const earningBarData = {
    labels: months,
    datasets: [
      {
        label: "Total Order Earning (₹)",
        data: monthlyEarnings?.map((d) => d.totalEarning) || [],
        backgroundColor: "rgba(255,101,132,0.7)",
        borderRadius: 4,
      },
      {
        label: "Beautician Earning (₹)",
        data: monthlyEarnings?.map((d) => d.beauticianEarning) || [],
        backgroundColor: "rgba(108,99,255,0.7)",
        borderRadius: 4,
      },
      {
        label: "Admin Earning (₹)",
        data: monthlyEarnings?.map((d) => d.adminEarning) || [],
        backgroundColor: "rgba(67,198,172,0.7)",
        borderRadius: 4,
      },
    ],
  };

  const beauticianStatusData = {
    labels: ["Active", "Inactive", "Blocked", "Deleted"],
    datasets: [
      {
        data: [
          beauticianStats?.active || 0,
          beauticianStats?.inactive || 0,
          beauticianStats?.blocked || 0,
          beauticianStats?.deleted || 0,
        ],
        backgroundColor: ["#22c55e", "#f59e0b", "#ef4444", "#94a3b8"],
        borderWidth: 0,
      },
    ],
  };

  const regSourceData = {
    labels: ["App", "Web"],
    datasets: [
      {
        data: [beauticianStats?.appReg || 0, beauticianStats?.webReg || 0],
        backgroundColor: ["#FF6584", "#6C63FF"],
        borderWidth: 0,
      },
    ],
  };

  const citySalesBarData = {
    labels: citySales?.map((c) => c.cityName || "Unknown") || [],
    datasets: [
      {
        label: "Sales (₹)",
        data: citySales?.map((c) => c.totalSales) || [],
        backgroundColor: [
          "#FF6584",
          "#6C63FF",
          "#43C6AC",
          "#F7971E",
          "#22c55e",
          "#ef4444",
          "#f59e0b",
          "#8b5cf6",
          "#06b6d4",
          "#ec4899",
        ],
        borderRadius: 6,
      },
    ],
  };

  const citySalesOpts = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            ` ₹${ctx.raw.toLocaleString()} · ${
              citySales?.[ctx.dataIndex]?.orderCount || 0
            } orders`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          color: theme.palette.text.secondary,
          font: { size: 10 },
          callback: (v) => `₹${v.toLocaleString()}`,
        },
        grid: { color: theme.palette.divider },
      },
      y: {
        ticks: {
          color: theme.palette.text.secondary,
          font: { size: 11, weight: "600" },
        },
        grid: { display: false },
      },
    },
  };

  const doughnutOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "bottom", labels: { font: { size: 11 } } } },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* ── KPI ROW 1 — Beautician counts ── */}
        <Grid container spacing={2.5} mb={3}>
          {[
            {
              icon: <GroupIcon sx={{ color: "#FF6584", fontSize: 26 }} />,
              label: "Total Beauticians",
              value: beauticianStats?.total?.toLocaleString(),
              sub: `+${beauticianStats?.todayReg || 0} today`,
            },
            {
              icon: <GroupIcon sx={{ color: "#22c55e", fontSize: 26 }} />,
              label: "Active",
              value: beauticianStats?.active?.toLocaleString(),
              bg: "#f0fdf4",
            },
            {
              icon: <GroupIcon sx={{ color: "#f59e0b", fontSize: 26 }} />,
              label: "Inactive",
              value: beauticianStats?.inactive?.toLocaleString(),
              bg: "#fffbeb",
              subColor: "#f59e0b",
            },
            {
              icon: <GroupIcon sx={{ color: "#ef4444", fontSize: 26 }} />,
              label: "Blocked",
              value: beauticianStats?.blocked?.toLocaleString(),
              bg: "#fef2f2",
              subColor: "#ef4444",
            },
            {
              icon: <GroupIcon sx={{ color: "#94a3b8", fontSize: 26 }} />,
              label: "Deleted",
              value: beauticianStats?.deleted?.toLocaleString(),
              bg: "#f8fafc",
              subColor: "#94a3b8",
            },
            {
              icon: <SpaIcon sx={{ color: "#FF6584", fontSize: 26 }} />,
              label: "Registered App",
              value: beauticianStats?.appReg?.toLocaleString(),
            },
            {
              icon: <SpaIcon sx={{ color: "#6C63FF", fontSize: 26 }} />,
              label: "Registered Web",
              value: beauticianStats?.webReg?.toLocaleString(),
            },
            {
              icon: <GroupIcon sx={{ color: "#442141", fontSize: 26 }} />,
              label: "Logins Today",
              value: beauticianLogins?.today?.toLocaleString(),
              sub: `Total ${beauticianLogins?.total?.toLocaleString()}`,
            },
          ].map((c, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <KpiCard {...c} />
            </Grid>
          ))}
        </Grid>

        {/* ── KPI ROW 2 — Orders & Earnings ── */}
        <Grid container spacing={2.5} mb={3}>
          {[
            {
              icon: (
                <ShoppingCartIcon sx={{ color: "#FF6584", fontSize: 26 }} />
              ),
              label: "Total Beauty Orders",
              value: orderSummary?.total?.toLocaleString(),
              sub: `+${orderSummary?.today || 0} today`,
            },
            {
              icon: (
                <ShoppingCartIcon sx={{ color: "#22c55e", fontSize: 26 }} />
              ),
              label: "Completed Orders",
              value: orderSummary?.completed?.toLocaleString(),
              bg: "#f0fdf4",
            },
            {
              icon: (
                <ShoppingCartIcon sx={{ color: "#ef4444", fontSize: 26 }} />
              ),
              label: "Cancelled Orders",
              value: orderSummary?.cancelled?.toLocaleString(),
              bg: "#fef2f2",
              subColor: "#ef4444",
            },
            {
              icon: (
                <CurrencyRupeeIcon sx={{ color: "#FF6584", fontSize: 26 }} />
              ),
              label: "Earn Today",
              value: `₹${(orderSummary?.todayEarn || 0).toLocaleString()}`,
            },
            {
              icon: (
                <CurrencyRupeeIcon sx={{ color: "#6C63FF", fontSize: 26 }} />
              ),
              label: "Total Earning",
              value: `₹${(orderSummary?.totalEarn || 0).toLocaleString()}`,
            },
            {
              icon: (
                <CurrencyRupeeIcon sx={{ color: "#22c55e", fontSize: 26 }} />
              ),
              label: "Beautician Total Earn",
              value: `₹${(
                orderSummary?.beauticianTotalEarn || 0
              ).toLocaleString()}`,
              bg: "#f0fdf4",
            },
            {
              icon: (
                <CurrencyRupeeIcon sx={{ color: "#442141", fontSize: 26 }} />
              ),
              label: "Admin Total Earn",
              value: `₹${(orderSummary?.adminTotalEarn || 0).toLocaleString()}`,
            },
            {
              icon: (
                <AccountBalanceWalletIcon
                  sx={{
                    color:
                      walletStats?.totalBalance >= 0 ? "#22c55e" : "#ef4444",
                    fontSize: 26,
                  }}
                />
              ),
              label: "Beautician Wallet Total",
              value: `₹${(walletStats?.totalBalance || 0).toLocaleString()}`,
              sub: `+${walletStats?.positiveCount || 0} pos · ${
                walletStats?.negativeCount || 0
              } neg`,
              subColor: walletStats?.totalBalance >= 0 ? "#22c55e" : "#ef4444",
            },
          ].map((c, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <KpiCard {...c} />
            </Grid>
          ))}
        </Grid>

        {/* ── KPI ROW 3 — Services & Calls ── */}
        <Grid container spacing={2.5} mb={4}>
          {[
            {
              icon: <InventoryIcon sx={{ color: "#FF6584", fontSize: 26 }} />,
              label: "Service Products",
              value: serviceCounts?.products?.toLocaleString(),
            },
            {
              icon: <InventoryIcon sx={{ color: "#6C63FF", fontSize: 26 }} />,
              label: "Product Packages",
              value: serviceCounts?.productPackages?.toLocaleString(),
            },
            {
              icon: <SpaIcon sx={{ color: "#43C6AC", fontSize: 26 }} />,
              label: "Beauty Packages",
              value: serviceCounts?.beautyPackages?.toLocaleString(),
            },
            {
              icon: <SpaIcon sx={{ color: "#F7971E", fontSize: 26 }} />,
              label: "Package Services",
              value: serviceCounts?.packageServices?.toLocaleString(),
            },
            {
              icon: <PhoneInTalkIcon sx={{ color: "#FF6584", fontSize: 26 }} />,
              label: "Total Calls",
              value: callChatStats?.totalCalls?.toLocaleString(),
              sub: `Active: ${callChatStats?.activeCalls || 0}`,
            },
            {
              icon: <PhoneInTalkIcon sx={{ color: "#22c55e", fontSize: 26 }} />,
              label: "Ended Calls",
              value: callChatStats?.endedCalls?.toLocaleString(),
              bg: "#f0fdf4",
            },
            {
              icon: <ChatIcon sx={{ color: "#6C63FF", fontSize: 26 }} />,
              label: "Total Chats",
              value: callChatStats?.totalChats?.toLocaleString(),
            },
            {
              icon: <ChatIcon sx={{ color: "#22c55e", fontSize: 26 }} />,
              label: "Active Chats",
              value: callChatStats?.activeChats?.toLocaleString(),
              bg: "#f0fdf4",
            },
          ].map((c, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <KpiCard {...c} />
            </Grid>
          ))}
        </Grid>

        {/* ── EARNINGS BAR CHART ── */}
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            mb: 3,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          }}
        >
          <SC>Monthly Earnings — Total vs Beautician vs Admin</SC>
          <Box sx={{ height: 320 }}>
            <Bar data={earningBarData} options={chartOpts(theme)} />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            {[
              {
                label: "Total Earning",
                value: `₹${(orderSummary?.totalEarn || 0).toLocaleString()}`,
                color: "#FF6584",
              },
              {
                label: "Beautician Earn (80%)",
                value: `₹${(
                  orderSummary?.beauticianTotalEarn || 0
                ).toLocaleString()}`,
                color: "#6C63FF",
              },
              {
                label: "Admin Earn (20%)",
                value: `₹${(
                  orderSummary?.adminTotalEarn || 0
                ).toLocaleString()}`,
                color: "#43C6AC",
              },
              {
                label: "Today Earning",
                value: `₹${(orderSummary?.todayEarn || 0).toLocaleString()}`,
                color: "#F7971E",
              },
            ].map((s, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Box
                  sx={{
                    background: "#fdf0f5",
                    borderRadius: 2,
                    p: 1.5,
                    textAlign: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: s.color,
                      mx: "auto",
                      mb: 0.5,
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    {s.label}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="#442141">
                    {s.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Card>

        {/* ── CITY SALES CHART ── */}
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            mb: 3,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
            flexWrap="wrap"
            gap={1}
          >
            <SC>City-wise Sales</SC>
            <ToggleButtonGroup
              value={citySalesPeriod}
              exclusive
              onChange={(_, v) => v && setCitySalesPeriod(v)}
              size="small"
              sx={{
                "& .MuiToggleButton-root": {
                  px: 2,
                  py: 0.5,
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: "none",
                  borderColor: "#FF658444",
                  color: "#442141",
                  "&.Mui-selected": {
                    background: "#FF6584",
                    color: "#fff",
                    borderColor: "#FF6584",
                  },
                },
              }}
            >
              <ToggleButton value="week">This Week</ToggleButton>
              <ToggleButton value="month">This Month</ToggleButton>
              <ToggleButton value="year">This Year</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {citySales?.length > 0 ? (
            <Box sx={{ height: Math.max(300, (citySales?.length || 0) * 44) }}>
              <Bar data={citySalesBarData} options={citySalesOpts} />
            </Box>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height={120}
            >
              <Typography color="text.secondary" variant="body2">
                No sales data for this period.
              </Typography>
            </Box>
          )}
          {citySales?.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={1.5}>
                {citySales.map((c, i) => (
                  <Grid item xs={6} sm={4} md={2.4} key={i}>
                    <Box
                      sx={{
                        background: "#fdf0f5",
                        borderRadius: 2,
                        p: 1.2,
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="#442141"
                        noWrap
                        display="block"
                      >
                        {c.cityName || "Unknown"}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={800}
                        color="#FF6584"
                      >
                        ₹{(c.totalSales || 0).toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {c.orderCount} orders
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Card>

        {/* ── STATUS DOUGHNUT + REG SOURCE + CITY COUNT ── */}
        <Grid container spacing={2.5} mb={3}>
          <Grid item xs={12} md={3}>
            <Card
              sx={{
                p: 3,
                borderRadius: 3,
                height: "100%",
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
              }}
            >
              <SC>Beautician Status</SC>
              <Box sx={{ height: 200 }}>
                <Doughnut data={beauticianStatusData} options={doughnutOpts} />
              </Box>
              <Divider sx={{ my: 1.5 }} />
              {[
                ["Active", beauticianStats?.active, "#22c55e"],
                ["Inactive", beauticianStats?.inactive, "#f59e0b"],
                ["Blocked", beauticianStats?.blocked, "#ef4444"],
                ["Deleted", beauticianStats?.deleted, "#94a3b8"],
              ].map(([k, v, c]) => (
                <Box
                  key={k}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.4}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: c,
                      }}
                    />
                    <Typography variant="caption" fontWeight={600}>
                      {k}
                    </Typography>
                  </Box>
                  <Chip
                    label={v || 0}
                    size="small"
                    sx={{ fontWeight: 700, fontSize: 11 }}
                  />
                </Box>
              ))}
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card
              sx={{
                p: 3,
                borderRadius: 3,
                height: "100%",
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
              }}
            >
              <SC>Registration Source</SC>
              <Box sx={{ height: 200 }}>
                <Doughnut data={regSourceData} options={doughnutOpts} />
              </Box>
              <Divider sx={{ my: 1.5 }} />
              {[
                ["App", beauticianStats?.appReg, "#FF6584"],
                ["Web", beauticianStats?.webReg, "#6C63FF"],
              ].map(([k, v, c]) => (
                <Box
                  key={k}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.4}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: c,
                      }}
                    />
                    <Typography variant="caption" fontWeight={600}>
                      {k}
                    </Typography>
                  </Box>
                  <Chip
                    label={v || 0}
                    size="small"
                    sx={{ fontWeight: 700, fontSize: 11 }}
                  />
                </Box>
              ))}
              <Divider sx={{ my: 1.5 }} />
              <Box
                sx={{
                  background: "#fdf0f5",
                  borderRadius: 2,
                  p: 1.2,
                  textAlign: "center",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Wallet Balance
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color={walletStats?.totalBalance >= 0 ? "#22c55e" : "#ef4444"}
                >
                  ₹{(walletStats?.totalBalance || 0).toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  +{walletStats?.positiveCount || 0} pos ·{" "}
                  {walletStats?.negativeCount || 0} neg ·{" "}
                  {walletStats?.zeroCount || 0} zero
                </Typography>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                p: 3,
                borderRadius: 3,
                height: "100%",
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <LocationOnIcon sx={{ color: "#FF6584" }} />
                <SC>Beautician Count by City</SC>
              </Box>
              <TableContainer>
                <Table sx={{ tableLayout: "fixed", width: "100%" }}>
                  <colgroup>
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "65%" }} />
                    <col style={{ width: "25%" }} />
                  </colgroup>
                  <TH cols={["#", "City", "Count"]} />
                  <TableBody>
                    {beauticianCityCount?.map((c, i) => (
                      <TableRow key={i} hover>
                        <TableCell sx={{ px: 2, py: 1 }}>
                          <Typography variant="body2">{i + 1}</Typography>
                        </TableCell>
                        <TableCell sx={{ px: 2, py: 1 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {c.cityName || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ px: 2, py: 1 }}>
                          <Chip
                            label={c.count}
                            size="small"
                            color="secondary"
                            sx={{ fontWeight: 700 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>

        {/* ── SERVICE COUNTS ── */}
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            mb: 3,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          }}
        >
          <SC>Beauty Services Overview</SC>
          <Grid container spacing={2}>
            {[
              {
                label: "Service Products (products)",
                value: serviceCounts?.products,
                color: "#FF6584",
                icon: <InventoryIcon sx={{ color: "#FF6584" }} />,
              },
              {
                label: "Product Packages (productpackages)",
                value: serviceCounts?.productPackages,
                color: "#6C63FF",
                icon: <SpaIcon sx={{ color: "#6C63FF" }} />,
              },
              {
                label: "Beauty Packages (beauty_packages)",
                value: serviceCounts?.beautyPackages,
                color: "#43C6AC",
                icon: <SpaIcon sx={{ color: "#43C6AC" }} />,
              },
              {
                label: "Package Services (packages_services)",
                value: serviceCounts?.packageServices,
                color: "#F7971E",
                icon: <InventoryIcon sx={{ color: "#F7971E" }} />,
              },
            ].map((s, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Box
                  sx={{
                    background: "#fdf0f5",
                    borderRadius: 3,
                    p: 2.5,
                    textAlign: "center",
                    border: `2px solid ${s.color}22`,
                  }}
                >
                  <Box sx={{ mb: 1 }}>{s.icon}</Box>
                  <Typography variant="h3" fontWeight={800} color={s.color}>
                    {s.value || 0}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    {s.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Card>

        {/* ── CALL & CHAT ── */}
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            mb: 3,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          }}
        >
          <SC>Call Sessions & Chat (per Order)</SC>
          <Grid container spacing={2}>
            {[
              {
                label: "Total Call Sessions",
                value: callChatStats?.totalCalls,
                color: "#FF6584",
              },
              {
                label: "Active Calls",
                value: callChatStats?.activeCalls,
                color: "#22c55e",
              },
              {
                label: "Ended Calls",
                value: callChatStats?.endedCalls,
                color: "#94a3b8",
              },
              {
                label: "Total Chats",
                value: callChatStats?.totalChats,
                color: "#6C63FF",
              },
              {
                label: "Active Chats",
                value: callChatStats?.activeChats,
                color: "#43C6AC",
              },
            ].map((s, i) => (
              <Grid item xs={6} sm={4} md={2} key={i}>
                <Box
                  sx={{
                    background: "#fdf0f5",
                    borderRadius: 2,
                    p: 1.5,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h4" fontWeight={800} color={s.color}>
                    {s.value || 0}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    {s.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Card>

        {/* ── LATEST BEAUTICIANS ── */}
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            mb: 3,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          }}
        >
          <SC>Latest Registered Beauticians (Last 10)</SC>
          <TableContainer>
            <Table sx={{ tableLayout: "fixed", width: "100%" }}>
              <colgroup>
                <col style={{ width: "5%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "10%" }} />
              </colgroup>
              <TH
                cols={[
                  "#",
                  "Name",
                  "Mobile",
                  "Email",
                  "Source",
                  "Status",
                  "Joined",
                ]}
              />
              <TableBody>
                {latestBeauticians?.map((b, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Typography variant="body2">{i + 1}</Typography>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: 12,
                            flexShrink: 0,
                            background: "#FF6584",
                          }}
                        >
                          {b.name?.[0] || "B"}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {b.name || "—"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Typography variant="body2" noWrap>
                        {b.mobile || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Typography variant="body2" noWrap>
                        {b.email || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Chip
                        label={b.regFrom || "—"}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 10 }}
                      />
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>{SC2(b.status)}</TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {b.created
                          ? new Date(b.created).toLocaleDateString()
                          : "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* ── LATEST BEAUTY ORDERS ── */}
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            mb: 3,
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          }}
        >
          <SC>Latest Beauty Orders (Last 10)</SC>
          <TableContainer>
            <Table sx={{ tableLayout: "fixed", width: "100%" }}>
              <colgroup>
                <col style={{ width: "16%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "17%" }} />
              </colgroup>
              <TH
                cols={[
                  "Customer",
                  "Beautician",
                  "Amount",
                  "Grand Total",
                  "Payment",
                  "Pay Status",
                  "Order Status",
                ]}
              />
              <TableBody>
                {latestBeautyOrders?.map((o, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Typography variant="body2" fontWeight={500} noWrap>
                        {o.user?.name || "Guest"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Typography variant="body2" noWrap>
                        {o.assignedBeautician?.name || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Typography variant="body2">
                        ₹{(o.totalAmount || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Typography variant="body2" fontWeight={700}>
                        ₹{(o.grandTotal || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      <Chip
                        label={(o.paymentMethod || "").toUpperCase()}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      {SC2(o.paymentStatus)}
                    </TableCell>
                    <TableCell sx={{ px: 2, py: 1 }}>
                      {SC2(o.serviceStatus)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default BeautyDashboard;
